import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import { bootstrapApplication } from '@angular/platform-browser';
import { environment } from '@core/environments/environment';
import { EEnvironmentName } from '@core/environments/environment.defaults';
import { suppressClarityConsoleErrors } from '@core/utils/clarity-console-error-filter';
import { startEarlyAppVersionCheck } from '@core/utils/early-app-version-check';
import { NgswRecoveryReporter } from '@core/utils/ngsw-recovery-reporter';
import * as Sentry from '@sentry/angular';
import { AppComponent } from './app/app.component';
import { appConfig } from './app/app.config';

// Removes the static, no-JS-dependency boot loader inlined in index.html. Only used here for
// the bootstrap-failure path - the success path is handled by AppComponent instead, which waits
// for the first route navigation to finish (not just for Angular to bootstrap), so the loader
// stays up through auth/permission guard redirects instead of disappearing before there's
// anything real to show.
function removeBootLoader(): void {
  document.getElementById('boot-loader')?.remove();
}

declare global {
  // eslint-disable-next-line @typescript-eslint/naming-convention -- must be named Window to merge with the global lib.dom.d.ts interface
  interface Window {
    ngswWatchdogDisarm?: () => void;
  }
}

// Reaching this line proves the app bundle was actually delivered and parsed, which rules
// out a stuck service worker as the cause of anything that happens from here on (e.g. a
// slow backend call). See the watchdog script in index.html for the failure mode this
// guards against, and why it must be disarmed this early rather than after bootstrap.
window.ngswWatchdogDisarm?.();

// Fire-and-forget, non-blocking version probe - see early-app-version-check.ts for why this
// starts before bootstrapApplication() rather than from inside AppUpdateService.init().
startEarlyAppVersionCheck();
suppressClarityConsoleErrors();

const sentryConfig = environment.sentry;
const allowedEnvs = new Set([EEnvironmentName.production, EEnvironmentName.test, EEnvironmentName.branch]);

if (sentryConfig?.dsn && allowedEnvs.has(environment.environmentName ?? EEnvironmentName.local)) {
  Sentry.init({
    dsn: sentryConfig.dsn,
    environment: environment.environmentName ?? EEnvironmentName.local,
    maxValueLength: 10000,
    normalizeDepth: 20,
    profilesSampleRate: sentryConfig.profilesSampleRate ?? 0,
    release: environment.version,
    tracesSampleRate: sentryConfig.tracesSampleRate ?? 0,

    // Session Replay - tracks user interactions, clicks, navigation
    integrations: [
      Sentry.replayIntegration({
        blockAllMedia: true, // true to block media recordings
        maskAllText: true, // true to mask sensitive text
      }),
    ],
    replaysOnErrorSampleRate: sentryConfig.replaysOnErrorSampleRate ?? 1.0,
    replaysSessionSampleRate: sentryConfig.replaysSessionSampleRate ?? 0.1,

    // Enhanced user tracking
    beforeSend(event) {
      // Add additional context to events
      return event;
    },
  });
}

NgswRecoveryReporter.reportIfNeeded();

// German month/day names for date pipes that pass the UI language as their locale (English is built in).
registerLocaleData(localeDe);

bootstrapApplication(AppComponent, appConfig).catch((err) => {
  removeBootLoader();
  // eslint-disable-next-line no-console
  console.error(err);
});
